<template>
  <Dropdown
      ref="dropdownRef"
      popper-class="dropdown--datepicker"
      class="datepicker__input input-group w-100"
      :class="{disabled}"
      :distance="0"
      shift-cross-axis
      strategy="fixed"
      prevent-overflow
      handle-resize
      @close="onClose"
      @open="onShow"
  >
    <template #default="{ open, close }">
      <div class="form-control"
           :class="{disabled}"
           tabindex="0"
           @click="open"
      >
      <span v-if="!outputValue.length"
            class="input-placeholder"
      >
        {{ parsedPlaceholder }}
      </span>

        <div v-else
             class="datepicker__output"
             :class="{
              disabled,
              'datepicker__output--multi': range,
           }"
        >
          <slot v-if="name"
                name="input"
                v-bind="{ values: inputValues }"
          >
            <input v-for="val in inputValues"
                   :key="val"
                   type="hidden"
                   :disabled="disabled"
                   :name="parsedName"
                   :value="val"
            />
          </slot>
          <slot v-if="templateId"
                name="template"
                v-bind="{value: templateId}"
          >
            <input type="hidden" name="template_id" :value="templateId" />
          </slot>
          <span
              v-for="val in outputValue"
              :key="val"
              class="datepicker__value"
          >
          {{ val }}
        </span>
        </div>
      </div>

      <div class="input-group-text borderless-l">
        <i v-if="inputValues.length && clearable"
           class="icon-times action-icon"
           @click="clear"
        />
        <i v-else class="icon-calendar-o" @click="open" />
      </div>
    </template>


    <template #content="{ open, close }">
      <div class="datepicker"
           :class="{
              'datepicker--multi': showMultiCalendars,
              'datepicker--timepicker': timePicker
           }"
      >
        <div v-if="range"
             class="datepicker__header"
        >
          <i class="icon-calendar datepicker__icon theme-icon" />
          <span>{{ $t('global.select_date') }}</span>

          <i @click="close" class="icon-times datepicker__close action-icon control-icon ml-auto" />
        </div>

        <div class="datepicker__content"
             :class="{'datepicker__content--templates': enableTemplates}"
        >
          <div v-if="enableTemplates"
               class="datepicker__templates"
          >

            <div v-for="template in templatesList"
                 :key="template.id"
                 class="template"
                 :class="{'selected': templateId == template.id}"
                 @click="templateId = template.id"
            >
              {{ template.title }}
            </div>
          </div>

          <div class="datepicker__calendars">
            <div class="datepicker__template-select">
              <FormControl
                  type="select"
                  v-model="templateId"
                  :payload="templatesList"
                  label-disabled
              />
            </div>

            <VueDatePicker
                ref="datepickerRef"
                v-model="inputValue"
                :model-type="dateFormat"
                :multi-calendars="showMultiCalendars ? {} : null"
                :range="range"
                solo
                inline
                :dark="darkMode"
                time-picker-inline
                :enable-time-picker="timePicker"
                :locale="lang"
                :week-start="weekstart"
                :disable-year-select="dst"
                @internal-model-change="onDateChange"
            >
              <template #day="{ day, date }">
                <span>{{ day }}</span>
              </template>

              <template #calendar-icon>
                <i class="icon icon-calendar-o" />
              </template>

              <template #action-row="{ internalModelValue, selectDate }" />

              <template #time-picker="{ time, updateTime }">
                <DatepickerTimerpicker
                    :time="inputValue"
                    :date="date"
                    :range="range"
                    :dst="dst"
                    @selectFrom="onSelectFromTime"
                    @selectTo="onSelectToTime"
                />
              </template>
            </VueDatePicker>
          </div>
        </div>

        <div class="datepicker__footer">
          <Button class="ms-auto btn-primary btn-bordered"
                  @click="close"
          >
            {{ $t('global.cancel') }}
          </Button>
          <Button class="btn-primary"
                  @click="apply(close)"
          >
            {{ $t('global.set_date') }}
          </Button>
        </div>
      </div>
    </template>
  </Dropdown>
</template>
<script setup>
import VueDatePicker from '@vuepic/vue-datepicker';
import formControlProps from "@/js/Elements/Form/FormControlProps.js";
import {useFormControl, useFormControlParser} from "@/js/Elements/Form/FormControl.js";
import Button from "@/js/Elements/Button.vue";
import {computed, nextTick, onBeforeUnmount, ref, watch} from "vue";
import {useI18n} from "vue-i18n";
import DatepickerTimerpicker from "@/js/Elements/DatepickerTimerpicker.vue";
import {SCREEN_SM} from "@/js/constants.js";
import {parseDatepickerTemplate} from "@/js/helpers.js";
import {useLocale} from "@/js/Plugins/Locale.js";
import FormControl from "@/js/Elements/Form/FormControl.vue";
import Dropdown from "@/js/Elements/Dropdown.vue";
import * as locales from 'date-fns/locale';
import {useEmitter} from "@/js/Plugins/Emitter.js";
import moment from "moment";
import {useTheme} from "@/js/Plugins/Theme.js";

const { t } = useI18n();
const emit = defineEmits([
  'update:modelValue', 'update:rangeStart', 'update:rangeEnd',
  'focus', 'blur'
]);
const theme = useTheme();
const locale = useLocale();
const emitter = useEmitter();
const props = defineProps({
  ...formControlProps,
  dst: Boolean,
});

const templateId = ref(null);
const autoHide = ref(true);
const { inputValue } = useFormControl(props, emit);
const { parsedPlaceholder, parsedName } = useFormControlParser(props, {
  defaultPlaceholder: t('validation.datepicker_placeholder')
});
const date = ref(Array.isArray(props.modelValue) ? props.modelValue : [props.modelValue]);
const darkMode = computed(() => theme.isDarkMode());

const inputValues = computed(() => (Array.isArray(inputValue.value) ? inputValue.value : [inputValue.value]).filter(value => value).map(value => {
  return props.dst ? moment(value).format('MM-DD HH:mm') : value;
}));
const outputValue = computed(() => {
  return [...inputValues.value].map(value => moment(value).format(props.dst ? 'MM-DD HH:mm': (props.timePicker ? 'LLL' : 'LL')));
});

const dateFormat = computed(() => {
  if (!props.timePicker) return props.dateFormat;
  return `${props.dateFormat} ${props.timeFormat}`;
});

const momentFormat = computed(() => {
  if (!props.timePicker) return 'YYYY-MM-DD';
  return 'YYYY-MM-DD HH:mm';
});

const datepickerRef = ref();
const dropdownRef = ref();
const templatesList = ref();
const showMultiCalendars = computed(() => {
  return props.range && window.innerWidth > SCREEN_SM;
});
const lang = ref(locale.lang);
const weekstart = computed(() => locale.weekstart);
const formatLocale = ref({});
let clearTemplateValue = true;

emitter.on('locale:change', setupLocale);

onBeforeUnmount(() => {
  emitter.off('locale:change', setupLocale);
});

parseInputValue();
setupLocale();

if (props.templates) {
  watch(() => templateId.value, selectTemplate);
}

if (props.range) {
  watch(() => [props.rangeStart, props.rangeEnd], parseInputValue);
}

function setupLocale() {
  let key = locale.locale.split('_');
  lang.value = (typeof key[0] !== 'undefined' ? key[0] : 'en').toLowerCase();

  templatesList.value = JSON.parse(JSON.stringify(props.templates || [])).map(tmp => {
    tmp.title = t(tmp.title);
    return tmp;
  });
}

function parseInputValue() {
  const value = (props.range ? (Array.isArray(inputValue.value) ? inputValue.value : [props.rangeStart, props.rangeEnd]) : [inputValue.value])
      .filter(value => value && !isNaN(new Date(value)))
      .map((value) => {
        return moment(value).format(momentFormat.value);
      });

  inputValue.value = props.range ? value : (value.length ? value[0] : null);
}

function onSelectFromTime(time) {
  if (!time) return;
  const value = Array.isArray(date.value) ? date.value[0] : date.value
  let d = new Date(value);
  d.setHours(time[0]);
  d.setMinutes(time[1]);
  datepickerRef.value.updateInternalModelValue(props.range ? [d, (date.value[1] ? new Date(date.value[1]) : null)] : d);
}

function onSelectToTime(time) {
  if (!time || !props.range) return;
  const value = date.value[1];
  let d = new Date(value);
  d.setHours(time[0]);
  d.setMinutes(time[1]);
  datepickerRef.value.updateInternalModelValue([new Date(date.value[0]), d]);
}

function onDateChange(dateValue) {
  if (clearTemplateValue) {
    templateId.value = null;
  }

  date.value = [];
  (Array.isArray(dateValue) ? dateValue : [dateValue]).filter(v => v).forEach((value) => {
    date.value.push(moment(value).format(momentFormat.value));
  });
}

function clear(e) {
  e.stopPropagation();
  inputValue.value = props.range ? [null, null] : null;
  if (datepickerRef.value) {
    datepickerRef.value.updateInternalModelValue(inputValue.value);
  }
  apply();
}
function apply(close = true) {
  if (datepickerRef.value) {
    datepickerRef.value.selectDate();
  }

  if (props.range) {
    emit('update:rangeStart', inputValue.value[0]);
    emit('update:rangeEnd', inputValue.value[1]);
  }

  if (!dropdownRef.value || !close) return;
  dropdownRef.value.close();
}

function onClose() {
  emit('blur');
}

async function selectTemplate(id) {
  if (!id) return;
  const template = props.templates.find(t => t.id.toString() === id.toString());
  if (!template) return;

  const value = parseDatepickerTemplate(template);
  if (!value) return;

  if (!datepickerRef.value) return;

  clearTemplateValue = false;
  datepickerRef.value.updateInternalModelValue(value.map(v => new Date(v)));

  nextTick(() => {
    clearTemplateValue = true;
  });

  const viewDate = new Date(value[0]);
  datepickerRef.value.setMonthYear({
    month: viewDate.getMonth(),
    year: viewDate.getFullYear()
  });
}

function onShow() {
  emit('focus');
}

function onFocus(e) {
  if (props.disabled) return;
  emit('focus', e);
}
</script>
<style lang="scss">
@import '@vuepic/vue-datepicker/dist/main.css';
@import "@/css/functions";
@import "@/css/variables";
@import "bootstrap/scss/functions";
@import "bootstrap/scss/mixins";
@import "bootstrap/scss/variables";

.dropdown--datepicker {
  @media all and (min-width: 0) and (max-width: 576px) {
    top: spacing(4) !important;
    transform: none !important;
    left: spacing(4) !important;
    right: spacing(4) !important;
  }

  .v-popper__inner {
    border-radius: $border-radius-xxl;
    box-shadow: $soft-primary-shadow;
  }
}

</style>
<style lang="scss" scoped>
@import "@/css/functions";
@import "@/css/variables";
@import "bootstrap/scss/functions";
@import "bootstrap/scss/mixins";
@import "bootstrap/scss/variables";

$templatesSize:       toRem(170);
$columnWidth:         toRem(32);
$columnHeight:        toRem(32);
$headerColumnHeight:  toRem(26);

.datepicker {
  display: flex;
  flex-direction: column;
  cursor: pointer;

  &__close {
    font-size: toRem(14);
  }

  &__icon {
    font-size: toRem(18);
  }

  &__input {
    cursor: pointer;
    display: flex;
    align-items: center;
    border: none !important;
    height: $input-height;

    &.disabled,
    &.disabled .input-group-text{
      opacity: 1;
      background-color: $input-disabled-bg;
    }

    .form-control {
      overflow: hidden;
      text-overflow: ellipsis;
    }
  }

  &__content {
    display: flex;
    align-items: stretch;
    position: relative;
    flex-direction: column;

    @include media-breakpoint-up(sm) {
      &--templates .datepicker__calendars {
        min-height: toRem(356);
        margin-left: $templatesSize;
        flex-direction: row;
      }
    }
  }

  &__template-select {
    display: flex;
    padding: spacing(1);

    @include media-breakpoint-up(sm) {
      display: none;
    }
  }

  &__header,
  &__footer {
    display: flex;
    align-items: center;
    padding: 0 spacing(4);
  }

  &__header {
    display: flex;
    align-items: center;
    height: toRem(56);
    gap: spacing(2);
    border-bottom: themeColor('border') solid toRem(1);
    font-weight: $font-weight-medium;

    .icon-calendar {
      margin-bottom: toRem(1);
    }
  }

  &__output {
    color: themeColor('neutral-700');
    font-size: fontSize(5);
    display: inline-flex;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;

    &--multi {
      .datepicker__value:first-of-type {
        &:after {
          content: '‒';
          margin: 0 spacing(1);
        }
      }
    }
  }

  &__value {
    font-weight: $font-weight-medium;
    color: themeColor('text');
    white-space: nowrap;
  }

  &__templates {
    width: $templatesSize;
    position: absolute;
    top: 0;
    left: 0;
    bottom: 0;
    overflow-y: auto;
    background-color: themeColor('neutral-100');
    display: none;
    flex-direction: column;
    padding: spacing(5);
    cursor: default;
    gap: spacing(1);

    @include media-breakpoint-up(sm) {
      display: flex;
    }

    .template {
      height: toRem(33);
      flex-shrink: 0;
      display: flex;
      align-items: center;
      padding: 0 spacing(3);
      cursor: pointer;
      border-radius: $border-radius-lg;
      font-weight: $font-weight-base;
      border: transparent solid toRem(1);
      white-space:nowrap;
      text-overflow: ellipsis;
      overflow: hidden;

      &:hover {
        border-color: themeColor('primary');
      }

      &.selected {
        background-color: themeColor('primary');
        border: themeColor('primary') solid toRem(1);
        color: themeColor('base');
      }
    }
  }

  &__footer {
    gap: spacing(2);
    //height: toRem(60);
    padding: spacing(4) spacing(4);
    border-top: themeColor('border') solid toRem(1);
    cursor: default;
  }


  :deep(.dp__theme_light) {
    --dp-primary-color: #{themeColor('primary')};
    --dp-hover-color: #{themeColor('neutral-100')};
    --dp-multi-calendars-spacing: 0;
    --dp-background-color: #{themeColor('base')};
    --dp-text-color: #{themeColor('text')};
    --dp-hover-text-color: #{themeColor('text')};
    --dp-range-between-dates-text-color: #{themeColor('text')};

    .dp__calendar_item > .dp__cell_inner.dp__range_between,
    .dp__calendar_item > .dp__cell_inner.dp__range_start,
    .dp__calendar_item > .dp__cell_inner.dp__range_end {
      background-color: themeColor('base');
      @include media-breakpoint-up(sm) {
        background-color: themeColor('primary-100');
      }
    }

    .dp__calendar_item > .dp__cell_inner.dp__range_between {
      background-color: themeColor('primary-100');
    }

    .dp__calendar_item > .dp__cell_inner.dp__date_hover_end:hover {
      background-color: themeColor('primary-100');
    }
  }

  :deep(.dp__theme_dark) {
    --dp-primary-color: #{themeColor('primary')};
    --dp-background-color: #{themeColor('base')};
    --dp-hover-color: #{themeColor('neutral-100')};

    .dp__calendar_item > .dp__cell_inner.dp__range_between,
    .dp__calendar_item > .dp__cell_inner.dp__range_start,
    .dp__calendar_item > .dp__cell_inner.dp__range_end {
      background-color: #{themeColor('primary-800')};
      @include media-breakpoint-up(sm) {
        background-color: #{themeColor('primary-800')};
      }
    }

    .dp__calendar_item > .dp__cell_inner.dp__range_between {
      background-color: themeColor('primary-800');
    }

    .dp__calendar_item > .dp__cell_inner.dp__date_hover_end:hover {
      background-color: themeColor('primary-800');
    }
  }


  /**
      Calendar layout
   */
  &:deep(.dp__main > .dp__outer_menu_wrap > .dp__menu) {
    border: none;
  }

  &:deep(.dp__main > .dp__outer_menu_wrap > .dp__menu > div > .dp__instance_calendar) {
    padding: spacing(3) spacing(4);
    display: flex;
    flex-direction: column;
  }

  &:deep(.dp__main > .dp__outer_menu_wrap > .dp__menu > div > .dp__instance_calendar > .dp__menu_inner) {
    padding: 0;
    gap: spacing(2);
  }

  &:deep(.dp__main > .dp__outer_menu_wrap > .dp__menu > div > .dp__instance_calendar > .dp__menu_inner > .dp__instance_calendar) {
    border: themeColor('border') solid toRem(1);
    border-radius: $border-radius-xl;
    padding: spacing(1);
  }

  &:deep(.dp__month_year_row) {
    //height: toRem(42);
  }

  &:deep(.dp__month_year_row > .dp__btn.dp--arrow-btn-nav) {
    width: toRem(28);
    height: toRem(28);
    display: flex;
    justify-content: center;
    align-items: center;
    padding: 0;
  }

  &:deep(.dp__month_year_row > .dp__btn.dp--arrow-btn-nav > .dp__inner_nav) {
    width: 100%;
    height: 100%;
    display: flex;
    justify-content: center;
    align-items: center;
    border-radius: $border-radius-sm;
  }

  &:deep(.dp__month_year_row > .dp__btn.dp--arrow-btn-nav svg) {
    fill: themeColor('neutral-800')
  }

  &:deep(.dp__action_row) {
    display: none;
  }

  &:deep(.dp__month_year_row) {
    display: flex;
    align-items: center;
  }

  &:deep(.dp__btn.dp__month_year_select) {
    display: flex;
    align-items: center;
    border-radius: $border-radius-lg;
    font-weight: $font-weight-medium;
  }

  &:deep(.dp__month_year_wrap > .dp__btn:first-of-type) {
    justify-content: end;
    padding-right: spacing(2);
  }

  &:deep(.dp__month_year_wrap > .dp__btn:last-of-type) {
    justify-content: start;
    padding-left: spacing(2);
  }

  /**
      Rows and columns
   */

  &:deep(.dp__calendar_row) {
    margin: 0;
    margin-top: toRem(0);
  }

  &:deep(.dp__calendar_header_item),
  &:deep(.dp__calendar_item) {
    width: 100%;
    aspect-ratio: 1/1;
    max-height: $columnHeight;
    padding: 0;
    display: flex;
    justify-content: center;
    align-items: center;
    font-size: fontSize(4);
    color: themeColor('neutral-800');
    font-weight: $font-weight-medium;
    border: none;
    box-sizing: border-box;
  }

  @include media-breakpoint-up(sm) {
    &:deep(.dp__calendar_header_item),
    &:deep(.dp__calendar_item) {
      width: $columnWidth;
      max-width: $columnWidth;
      height: $columnHeight;
      max-height: $columnHeight;
    }
  }

  &:deep(.dp__calendar_header_item) {
    height: $headerColumnHeight;
    max-height: $headerColumnHeight;
    font-size: fontSize(5);
    color: themeColor('neutral-700');
  }

  &:deep(.dp__calendar_header_separator) {
    display: none;
  }

  &:deep(.dp__calendar_item > .dp__cell_inner.dp__cell_offset) {
    color: themeColor('neutral-400');
    font-weight: $font-weight-normal;
  }

  &:deep(.dp__calendar_item > .dp__cell_inner.dp__cell_offset.dp__range_start),
  &:deep(.dp__calendar_item > .dp__cell_inner.dp__cell_offset.dp__range_end) {
    color: themeColor('base');
    font-weight: $font-weight-normal;
  }

  &:deep(.dp__calendar_item > .dp__cell_inner) {
    width: 100%;
    height: 100%;
    border-radius: 50%;
  }

  &:deep(.dp__calendar_item > .dp__cell_inner > span) {
    width: $columnWidth;
    height: $columnHeight;
    display: flex;
    justify-content: center;
    align-items: center;
    position: absolute;
    top: 50%;
    left: 50%;
    border-radius: 50%;
    transform: translate(-50%, -50%);

    @include media-breakpoint-up(sm) {
      &:deep(.dp__calendar_header_item),
      &:deep(.dp__calendar_item) {
        width: 100%;
        height: 100%;
        top: 0;
        left: 0;
        transform: none;
      }
    }
  }

  &:deep(.dp__calendar_item:hover > .dp__cell_inner > span) {
    border: themeColor('primary') solid toRem(1);
  }

  &:deep(.dp__calendar_item > .dp__cell_inner.dp__range_between) {
    border-radius: 0;
    border: none;
  }

  &:deep(.dp__calendar_item > .dp__cell_inner.dp__today) {
    border-color: transparent;
  }
  &:deep(.dp__calendar_item > .dp__cell_inner.dp__today > span) {
    background-color: themeColor('neutral-100');
  }



  &:deep(.dp__calendar_item > .dp__cell_inner.dp__range_start),
  &:deep(.dp__calendar_item > .dp__cell_inner.dp__range_end) {
    border: none;

    &:before {
      content: '';
      width: 50%;
      position: absolute;
      top: 0;
      bottom: 0;
      background-color: themeColor('primary-100');
    }

    @include media-breakpoint-up(sm) {
      &:before { content: none; }
    }
  }

  &:deep(.dp__calendar_item > .dp__cell_inner.dp__range_start){
    border-top-right-radius: 0;
    border-bottom-right-radius: 0;
    &:before { right: 0; }
  }

  &:deep(.dp__calendar_item > .dp__cell_inner.dp__range_end) {
    border-top-left-radius: 0;
    border-bottom-left-radius: 0;
    &:before { left: 0; }
  }

  &:deep(.dp__calendar_item > .dp__cell_inner.dp__range_start.dp__range_end) {
    &:before { content: none; }
  }

  &:deep(.dp__calendar_item > .dp__cell_inner.dp__range_start > span),
  &:deep(.dp__calendar_item > .dp__cell_inner.dp__range_end > span),
  &:deep(.dp__calendar_item > .dp__cell_inner.dp__active_date > span){
    background-color: themeColor('primary');
  }

  &:deep(.dp__calendar_item > .dp__cell_inner.dp__date_hover_start:hover) {
    border-top-right-radius: 0;
    border-bottom-right-radius: 0;
    background-color: themeColor('primary-100');
  }

  &:deep(.dp__calendar_item > .dp__cell_inner.dp__date_hover_end:hover) {
    border-top-left-radius: 0;
    border-bottom-left-radius: 0;
  }

  &:deep(.dp__calendar_item > .dp__cell_inner.dp__range_start.dp__range_end) {
    border-radius: 50%;
  }

  /**
    Months / Years select
   */

  &:deep(.dp__overlay.dp--overlay-absolute) {
    top: 0;
    left: 0;
    width: auto;
    height: auto;
    right: 0;
    bottom: spacing(1);
    border-radius: $border-radius-xl;
  }

  &:deep(.dp__overlay_col) {
    width: calc(100% / 3);
  }

  &:deep(.dp__overlay_row) {
    flex: 0;
    width: 100%;
  }

  &:deep(.dp__month_year_wrap .dp__overlay_container) {
    padding: spacing(1) spacing(1) 0 spacing(1);
  }

  &:deep(.dp__button.dp__overlay_action) {
    width: auto;
    left: 0;
    right: spacing(2);
    border-radius: $border-radius-lg;
    color: themeColor('neutral-800');
  }

  &:deep(.dp__overlay_cell_pad) {
    font-size: fontSize(4);
    font-weight: $font-weight-medium;
    border-radius: $border-radius-lg;
  }

  &:deep(.dp__overlay_cell.dp__overlay_cell_pad) {
    border: themeColor('border') solid toRem(1);
  }
}
</style>